import React from 'react';
import './ReadOnlyWrapper.scss';

export type IReadOnlyWrapperProps = {
    isReadOnly?: boolean;
    children?: React.ReactNode;
    className?: string;
};

const ReadOnlyWrapper = ({ children, isReadOnly, className }: IReadOnlyWrapperProps) => {
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!wrapperRef.current) return;

        const applyTabIndex = () => {
            if (!wrapperRef.current) return;
            
            const selector = 'input, button, select, textarea, label, .cursor--pointer, .btn';
            const elements = wrapperRef.current.querySelectorAll(selector);

            elements.forEach((element) => {
                const hasEnablePointerEvents = element.closest('.ReadOnlyWrapper--enable-pointer-events');
                
                if (isReadOnly && !hasEnablePointerEvents) {
                    // Store original tabindex before overwriting (only if not already stored)
                    if (!element.hasAttribute('data-original-tabindex')) {
                        const originalTabIndex = element.getAttribute('tabindex');
                        element.setAttribute('data-original-tabindex', originalTabIndex || 'null');
                    }
                    element.setAttribute('tabindex', '-1');
                } else {
                    // Restore original tabindex when not readonly
                    if (element.hasAttribute('data-original-tabindex')) {
                        const originalTabIndex = element.getAttribute('data-original-tabindex');
                        if (originalTabIndex === 'null') {
                            element.removeAttribute('tabindex');
                        } else {
                            element.setAttribute('tabindex', originalTabIndex!);
                        }
                        element.removeAttribute('data-original-tabindex');
                    }
                }
            });
        };

        applyTabIndex();

        if (!isReadOnly) return; // No need to observe DOM changes if not in read-only mode
        
        // Watch for DOM changes to prevent elements being focusable when they become visible 
        // after collapse/expand
        const observer = new MutationObserver(() => {
            applyTabIndex();
        });
        
        observer.observe(wrapperRef.current, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
        };
    }, [isReadOnly]);
    
    return (
        <div
            ref={wrapperRef}
            data-testid="read-only-wrapper"
            className={`ReadOnlyWrapper ${className} ${isReadOnly ? 'ReadOnlyWrapper--disabled' : ''}`}
        >
            {children}
        </div>
    );
};

export default ReadOnlyWrapper;
